/**
 * SOS / Panic incidents — admin queue + dashboard (V0.3).
 *
 * Surfaces:
 *   - KPI strip (open / acknowledged / resolved in window / avg response /
 *     false-alarm %).
 *   - TabsPills status filter + kind dropdown + date range.
 *   - Card list (not table — incidents are visual+urgent). Open incidents
 *     get a red header + live "time since raised" ticker.
 *   - Acknowledge (one-click), Resolve (notes dialog), False alarm (reason
 *     dialog).
 *   - Realtime: `panic:raised` → query invalidate + toast + audio chime + (if
 *     viewing the "open" filter) red banner asking the admin to take action.
 *   - Realtime: `panic:updated` → query invalidate (so sibling admins see
 *     state changes immediately when another desk acks/resolves).
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  ShieldAlert,
  Activity,
  HeartPulse,
  Flame,
  HelpCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, getErrorMessage } from '@/lib/api';
import { subscribe } from '@/lib/socket';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { TabsPills } from '@/components/ui/tabs-pills';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDateTime, formatRelative } from '@/lib/format';

type PanicStatus = 'open' | 'acknowledged' | 'resolved' | 'false_alarm';
type PanicKind = 'security' | 'medical' | 'fire' | 'other';

interface PanicIncident {
  id: string;
  society_id: string;
  raised_by_user_id: string | null;
  raised_by_kind: 'resident' | 'guard';
  raised_by_unit_id: string | null;
  raised_by_label: string;
  kind: PanicKind;
  description: string | null;
  location_hint: string | null;
  status: PanicStatus;
  acknowledged_at: string | null;
  acknowledged_by_user_id: string | null;
  responded_by_user_id: string | null;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  resolution_notes: string | null;
  false_alarm_reason: string | null;
  response_time_seconds: number | null;
  created_at: string;
  updated_at: string;
}

interface PanicStats {
  window_days: number;
  total_in_window: number;
  open: number;
  acknowledged: number;
  resolved_in_window: number;
  false_alarm_in_window: number;
  false_alarm_pct: number;
  avg_response_time_seconds: number | null;
  by_kind: Record<string, number>;
}

interface CursorList<T> {
  items: T[];
  next_cursor: string | null;
}

const KIND_ICON: Record<PanicKind, React.ComponentType<{ className?: string }>> = {
  security: ShieldAlert,
  medical: HeartPulse,
  fire: Flame,
  other: HelpCircle
};

const KIND_LABEL: Record<PanicKind, string> = {
  security: 'Security',
  medical: 'Medical',
  fire: 'Fire',
  other: 'Other'
};

const STATUS_VARIANT: Record<PanicStatus, 'alert' | 'warn' | 'ok' | 'muted'> = {
  open: 'alert',
  acknowledged: 'warn',
  resolved: 'ok',
  false_alarm: 'muted'
};

const STATUS_LABEL: Record<PanicStatus, string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  resolved: 'Resolved',
  false_alarm: 'False alarm'
};

// 880 Hz / 120 ms chime — base64 sine sample inline so we don't ship binary.
const CHIME_DATA_URI =
  'data:audio/wav;base64,UklGRiQEAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAEAAA=';

function playChime(): void {
  try {
    const audio = new Audio(CHIME_DATA_URI);
    audio.volume = 0.5;
    void audio.play().catch(() => undefined);
  } catch {
    /* autoplay may be blocked — silent fail */
  }
}

function formatDuration(secs: number | null): string {
  if (secs === null || secs === undefined) return '—';
  if (secs < 60) return `${secs}s`;
  const m = Math.round(secs / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

export function PanicIncidentsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<PanicStatus | 'all'>('open');
  const [kindFilter, setKindFilter] = useState<PanicKind | ''>('');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [bannerActive, setBannerActive] = useState(false);

  const filtersHash = useMemo(
    () => JSON.stringify({ statusFilter, kindFilter, from, to }),
    [statusFilter, kindFilter, from, to]
  );
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const listQuery = useQuery({
    queryKey: ['panic-incidents', { statusFilter, kindFilter, from, to, cursor }],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (statusFilter !== 'all') qs.set('status', statusFilter);
      if (kindFilter) qs.set('kind', kindFilter);
      if (from) qs.set('from', new Date(from).toISOString());
      if (to) qs.set('to', new Date(`${to}T23:59:59`).toISOString());
      if (cursor) qs.set('cursor', cursor);
      return get<CursorList<PanicIncident>>(`/panic-incidents?${qs.toString()}`);
    },
    placeholderData: (prev) => prev
  });

  const statsQuery = useQuery({
    queryKey: ['panic-incidents', 'stats', 30],
    queryFn: () => get<PanicStats>('/panic-incidents/stats?days=30'),
    refetchInterval: 60_000
  });

  const { items, nextCursor } = useCursorAccumulator(listQuery.data, cursor, filtersHash);

  // Realtime: panic:raised on a new incident. Always invalidate + chime +
  // toast. If we're currently viewing the "open" filter, flip a red banner so
  // the admin can't miss it.
  useEffect(() => {
    const offRaised = subscribe('panic:raised', (e) => {
      if (e.type !== 'panic:raised') return;
      playChime();
      const kind = (e.panic_kind ?? 'security') as PanicKind;
      toast.error(`Panic alert: ${KIND_LABEL[kind] ?? 'Security'}`, {
        description: `Raised at ${new Date(e.ts).toLocaleTimeString()}. Coordinate response now.`,
        duration: 12_000
      });
      if (statusFilter === 'open' || statusFilter === 'all') {
        setBannerActive(true);
      }
      void queryClient.invalidateQueries({ queryKey: ['panic-incidents'] });
    });
    return () => offRaised();
  }, [queryClient, statusFilter]);

  // panic:updated event is custom (not declared in shared-types). The
  // typed subscribe() helper rejects unknown event names, so we drop down
  // to the raw socket on/off here. No toast — the user just took the
  // action; we only invalidate queries so peer dashboards stay in sync.
  useEffect(() => {
    let cancelled = false;
    const cleanupRef: { current: (() => void) | null } = { current: null };
    void (async () => {
      const { getSocket } = await import('@/lib/socket');
      if (cancelled) return;
      const s = getSocket();
      const handler = (): void => {
        void queryClient.invalidateQueries({ queryKey: ['panic-incidents'] });
      };
      s.on('panic:updated', handler);
      cleanupRef.current = () => s.off('panic:updated', handler);
    })();
    return () => {
      cancelled = true;
      cleanupRef.current?.();
    };
  }, [queryClient]);

  const [resolveTarget, setResolveTarget] = useState<PanicIncident | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [falseTarget, setFalseTarget] = useState<PanicIncident | null>(null);
  const [falseReason, setFalseReason] = useState('');

  const ackMutation = useMutation({
    mutationFn: (id: string) => post<PanicIncident>(`/panic-incidents/${id}/acknowledge`, {}),
    onSuccess: () => {
      toast.success('Incident acknowledged');
      void queryClient.invalidateQueries({ queryKey: ['panic-incidents'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const resolveMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      post<PanicIncident>(`/panic-incidents/${id}/resolve`, { resolution_notes: notes }),
    onSuccess: () => {
      toast.success('Incident resolved');
      setResolveTarget(null);
      setResolutionNotes('');
      void queryClient.invalidateQueries({ queryKey: ['panic-incidents'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const falseAlarmMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      post<PanicIncident>(`/panic-incidents/${id}/false-alarm`, { false_alarm_reason: reason }),
    onSuccess: () => {
      toast.success('Marked as false alarm');
      setFalseTarget(null);
      setFalseReason('');
      void queryClient.invalidateQueries({ queryKey: ['panic-incidents'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const stats = statsQuery.data;

  return (
    <PageHeader
      title="SOS / Panic incidents"
      description="Live queue and history of resident + guard panic alerts. Acknowledge, resolve, or close as false alarm."
    >
      {bannerActive ? (
        <div className="mb-vt-4 flex items-center justify-between gap-vt-3 rounded-vt-md border border-vt-alert/40 bg-vt-alert-100 p-vt-4 text-vt-alert">
          <div className="flex items-center gap-vt-3">
            <AlertOctagon className="h-5 w-5" />
            <div>
              <div className="text-vt-14 font-semibold">A new panic alert just landed</div>
              <div className="text-vt-12">Scroll to the top of the queue to triage.</div>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setBannerActive(false)}>
            Dismiss
          </Button>
        </div>
      ) : null}

      {/* KPI strip */}
      <div className="mb-vt-5 grid grid-cols-2 gap-vt-3 sm:grid-cols-3 lg:grid-cols-5">
        <KpiTile
          label="Open now"
          value={stats?.open}
          tone="alert"
          icon={<AlertOctagon className="h-4 w-4" />}
          loading={statsQuery.isLoading}
        />
        <KpiTile
          label="Acknowledged"
          value={stats?.acknowledged}
          tone="warn"
          icon={<Activity className="h-4 w-4" />}
          loading={statsQuery.isLoading}
        />
        <KpiTile
          label="Resolved (30d)"
          value={stats?.resolved_in_window}
          tone="ok"
          icon={<CheckCircle2 className="h-4 w-4" />}
          loading={statsQuery.isLoading}
        />
        <KpiTile
          label="Avg response"
          value={stats?.avg_response_time_seconds !== null && stats?.avg_response_time_seconds !== undefined ? formatDuration(stats.avg_response_time_seconds) : '—'}
          tone="info"
          icon={<Activity className="h-4 w-4" />}
          loading={statsQuery.isLoading}
        />
        <KpiTile
          label="False alarms"
          value={stats ? `${stats.false_alarm_pct}%` : undefined}
          tone="muted"
          icon={<XCircle className="h-4 w-4" />}
          loading={statsQuery.isLoading}
        />
      </div>

      {/* Filters */}
      <div className="mb-vt-4 flex flex-wrap items-end gap-vt-3">
        <TabsPills
          aria-label="Status filter"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as PanicStatus | 'all')}
          options={[
            { value: 'open', label: 'Open', count: stats?.open },
            { value: 'acknowledged', label: 'Acknowledged', count: stats?.acknowledged },
            { value: 'resolved', label: 'Resolved' },
            { value: 'false_alarm', label: 'False alarm' },
            { value: 'all', label: 'All' }
          ]}
        />
        <div className="flex flex-col gap-vt-1">
          <Label htmlFor="panic-kind">Kind</Label>
          <Select
            id="panic-kind"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as PanicKind | '')}
            className="w-40"
          >
            <option value="">All kinds</option>
            <option value="security">Security</option>
            <option value="medical">Medical</option>
            <option value="fire">Fire</option>
            <option value="other">Other</option>
          </Select>
        </div>
        <div className="flex flex-col gap-vt-1">
          <Label htmlFor="panic-from">From</Label>
          <input
            id="panic-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-9 rounded-vt-sm border border-vt-border bg-vt-surface px-vt-2 text-vt-13"
          />
        </div>
        <div className="flex flex-col gap-vt-1">
          <Label htmlFor="panic-to">To</Label>
          <input
            id="panic-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-9 rounded-vt-sm border border-vt-border bg-vt-surface px-vt-2 text-vt-13"
          />
        </div>
        {(kindFilter || from || to || statusFilter !== 'open') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setKindFilter('');
              setFrom('');
              setTo('');
              setStatusFilter('open');
            }}
          >
            Reset
          </Button>
        )}
      </div>

      {/* List */}
      {listQuery.isLoading ? (
        <div className="flex flex-col gap-vt-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-36 w-full" />
          ))}
        </div>
      ) : listQuery.isError ? (
        <Card>
          <CardContent className="text-vt-alert">{getErrorMessage(listQuery.error)}</CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={<AlertTriangle className="h-7 w-7" />}
              title="No incidents in this window"
              description="Resident + guard SOS raises will appear here. Stay safe."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-vt-3">
          {items.map((incident) => (
            <IncidentCard
              key={incident.id}
              incident={incident}
              busyId={ackMutation.variables ?? null}
              ackPending={ackMutation.isPending}
              onAcknowledge={() => ackMutation.mutate(incident.id)}
              onResolve={() => {
                setResolveTarget(incident);
                setResolutionNotes('');
              }}
              onFalseAlarm={() => {
                setFalseTarget(incident);
                setFalseReason('');
              }}
            />
          ))}
        </div>
      )}

      <CursorPaginator
        hasMore={Boolean(nextCursor)}
        loading={listQuery.isFetching}
        itemsShown={items.length}
        onLoadMore={() => nextCursor && setCursor(nextCursor)}
      />

      {/* Resolve dialog */}
      <Dialog
        open={Boolean(resolveTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setResolveTarget(null);
            setResolutionNotes('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve incident</DialogTitle>
            <DialogDescription>
              {resolveTarget ? (
                <>
                  Mark <strong>{resolveTarget.raised_by_label}</strong>'s alert as resolved.
                </>
              ) : (
                'Mark this incident as resolved.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-vt-2 py-vt-2">
            <Label htmlFor="panic-resolve-notes">Resolution notes</Label>
            <Textarea
              id="panic-resolve-notes"
              rows={4}
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder="What happened, who responded, outcome…"
              maxLength={1000}
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setResolveTarget(null)}
              disabled={resolveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!resolveTarget) return;
                resolveMutation.mutate({ id: resolveTarget.id, notes: resolutionNotes });
              }}
              disabled={resolveMutation.isPending}
            >
              <CheckCircle2 className="h-4 w-4" />
              Resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* False-alarm dialog */}
      <Dialog
        open={Boolean(falseTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setFalseTarget(null);
            setFalseReason('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as false alarm</DialogTitle>
            <DialogDescription>
              {falseTarget ? (
                <>
                  Close <strong>{falseTarget.raised_by_label}</strong>'s alert as a false alarm. Provide a brief reason for the record.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-vt-2 py-vt-2">
            <Label htmlFor="panic-false-reason">Reason</Label>
            <Textarea
              id="panic-false-reason"
              rows={3}
              value={falseReason}
              onChange={(e) => setFalseReason(e.target.value)}
              placeholder="e.g. Pocket-dial, child playing with device"
              maxLength={500}
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setFalseTarget(null)}
              disabled={falseAlarmMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (!falseTarget || !falseReason.trim()) {
                  toast.error('Reason is required');
                  return;
                }
                falseAlarmMutation.mutate({ id: falseTarget.id, reason: falseReason.trim() });
              }}
              disabled={falseAlarmMutation.isPending}
            >
              <XCircle className="h-4 w-4" />
              Mark false alarm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageHeader>
  );
}

function KpiTile({
  label,
  value,
  tone,
  icon,
  loading
}: {
  label: string;
  value: number | string | undefined;
  tone: 'alert' | 'warn' | 'ok' | 'info' | 'muted';
  icon: React.ReactNode;
  loading?: boolean;
}): JSX.Element {
  const TONE_CLS: Record<typeof tone, string> = {
    alert: 'bg-vt-alert-100 text-vt-alert',
    warn: 'bg-vt-warn-100 text-vt-warn',
    ok: 'bg-vt-safe-100 text-vt-safe',
    info: 'bg-vt-info-100 text-vt-info',
    muted: 'bg-vt-surface-2 text-vt-text-muted'
  };
  return (
    <Card>
      <CardContent className="p-vt-4">
        <div className="mb-vt-2 inline-flex items-center gap-vt-2 rounded-vt-pill px-2 py-1 text-vt-12 font-medium ring-1 ring-inset ring-vt-border">
          <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${TONE_CLS[tone]}`}>
            {icon}
          </span>
          {label}
        </div>
        <div className="text-vt-24 font-semibold text-vt-text">
          {loading ? <Skeleton className="h-7 w-16" /> : value ?? 0}
        </div>
      </CardContent>
    </Card>
  );
}

function IncidentCard({
  incident,
  busyId,
  ackPending,
  onAcknowledge,
  onResolve,
  onFalseAlarm
}: {
  incident: PanicIncident;
  busyId: string | null;
  ackPending: boolean;
  onAcknowledge: () => void;
  onResolve: () => void;
  onFalseAlarm: () => void;
}): JSX.Element {
  const KindIcon = KIND_ICON[incident.kind];
  const isOpen = incident.status === 'open';
  const isAck = incident.status === 'acknowledged';
  const isClosed = incident.status === 'resolved' || incident.status === 'false_alarm';

  // Live-ticking time-since-raised, but only for open incidents — closed
  // cards just show the fixed creation timestamp.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!isOpen) return;
    const t = setInterval(() => setTick((x) => x + 1), 30_000);
    return () => clearInterval(t);
  }, [isOpen]);

  const isBusy = busyId === incident.id && ackPending;

  return (
    <Card
      className={
        isOpen
          ? 'border-vt-alert/40 shadow-vt-2'
          : isAck
          ? 'border-vt-warn/40'
          : undefined
      }
    >
      {/* Open-state red header */}
      {isOpen ? (
        <div className="flex items-center justify-between gap-vt-3 rounded-t-vt-md bg-vt-alert px-vt-4 py-vt-2 text-white">
          <div className="flex items-center gap-vt-2">
            <KindIcon className="h-5 w-5" />
            <span className="text-vt-14 font-semibold">
              {KIND_LABEL[incident.kind]} alert · {incident.raised_by_label}
            </span>
          </div>
          <span className="text-vt-12 opacity-90" key={tick}>
            {formatRelative(incident.created_at)}
          </span>
        </div>
      ) : null}

      <CardContent className="space-y-vt-3 p-vt-4">
        {!isOpen ? (
          <div className="flex items-start justify-between gap-vt-2">
            <div className="flex items-center gap-vt-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-vt-surface-2 text-vt-text-muted">
                <KindIcon className="h-4 w-4" />
              </span>
              <div>
                <div className="text-vt-14 font-semibold text-vt-text">
                  {KIND_LABEL[incident.kind]} · {incident.raised_by_label}
                </div>
                <div className="text-vt-12 text-vt-text-muted">
                  Raised {formatDateTime(incident.created_at)}
                </div>
              </div>
            </div>
            <Badge variant={STATUS_VARIANT[incident.status]}>{STATUS_LABEL[incident.status]}</Badge>
          </div>
        ) : null}

        {/* Description + location */}
        {(incident.description || incident.location_hint) && (
          <div className="grid grid-cols-1 gap-vt-2 sm:grid-cols-2">
            {incident.description ? (
              <div>
                <div className="text-vt-12 text-vt-text-muted">Description</div>
                <div className="text-vt-13 text-vt-text">{incident.description}</div>
              </div>
            ) : null}
            {incident.location_hint ? (
              <div>
                <div className="text-vt-12 text-vt-text-muted">Location hint</div>
                <div className="text-vt-13 text-vt-text">{incident.location_hint}</div>
              </div>
            ) : null}
          </div>
        )}

        {/* Closed-state timeline */}
        {isClosed ? (
          <div className="rounded-vt-sm border border-vt-border bg-vt-surface-2 p-vt-3 text-vt-12 text-vt-text-muted">
            <div>
              <strong className="text-vt-text">Raised</strong> {formatDateTime(incident.created_at)}
            </div>
            {incident.acknowledged_at ? (
              <div>
                <strong className="text-vt-text">Acknowledged</strong>{' '}
                {formatDateTime(incident.acknowledged_at)}
              </div>
            ) : null}
            {incident.resolved_at ? (
              <div>
                <strong className="text-vt-text">
                  {incident.status === 'false_alarm' ? 'Closed (false alarm)' : 'Resolved'}
                </strong>{' '}
                {formatDateTime(incident.resolved_at)} · response time{' '}
                {formatDuration(incident.response_time_seconds)}
              </div>
            ) : null}
            {incident.resolution_notes ? (
              <div className="mt-vt-2 rounded-vt-sm bg-vt-surface p-vt-2 text-vt-12 text-vt-text">
                {incident.resolution_notes}
              </div>
            ) : null}
            {incident.false_alarm_reason ? (
              <div className="mt-vt-2 rounded-vt-sm bg-vt-surface p-vt-2 text-vt-12 text-vt-text">
                <strong>False-alarm reason:</strong> {incident.false_alarm_reason}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Actions */}
        {!isClosed ? (
          <div className="flex flex-wrap justify-end gap-vt-2 pt-vt-1">
            {isOpen ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={onAcknowledge}
                disabled={isBusy}
              >
                <Activity className="h-4 w-4" />
                Acknowledge
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={onFalseAlarm}>
              <XCircle className="h-4 w-4" />
              False alarm
            </Button>
            <Button size="sm" onClick={onResolve}>
              <CheckCircle2 className="h-4 w-4" />
              Resolve
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
